import React from "react"
import Toggle from "react-toggle"
import { Button, Modal } from "react-bootstrap"
import { defineMessages, FormattedMessage, injectIntl } from "react-intl"
import MoneyInputForm from "../../common/form/moneyinput.component"
import PaymentTypeSelect from "../../payment-type/paymenttypeselect"
import PaymentCalculationComponent from "../components/PaymentCalculationComponent"
import {
    getTotalAmount,
    getPaymentType,
    getPaymentNotes,
    useDefaultCardOnFile,
    getCustomerIdSelection,
    getIgnorePaymentStep
} from "../../state/selection/selectionSelectors"
import { connect, ConnectedProps } from "react-redux"
import IntlProps from "../../state/helpers/IntlProps"
import {
    setIgnorePaymentStep,
    setPaymentNote,
    setPaymentType,
    setUseDefaultCardOnFile
} from "../../state/selection/selectionActions"
import ActionButton from "../../common/button/actionbutton.component.jsx"
import {isTypePriceListObjectWithOptionalPayment, PRICE_LIST_OBJECT_TYPE} from "../../state/price-list/Constants"

const messages = defineMessages({
    modal_title: {
        id: "price_list.select_payment_method_modal.title",
        defaultMessage: "Select payment method",
    },
    modal_back_btn: {
        id: "price_list.select_payment_method_modal.back_btn",
        defaultMessage: "Back",
    },
    modal_create_btn: {
        id: "price_list.select_payment_method_modal.create_btn",
        defaultMessage: "Create",
    },
    modal_note_label: {
        id: "price_list.select_payment_method_modal.note_label",
        defaultMessage: "Note",
    },
    modal_payment_type_label: {
        id: "invoice.receive_payment.payment_type_label",
        defaultMessage: "Payment type",
    },
    modal_amount_label: {
        id: "invoice.receive_payment.amount_label",
        defaultMessage: "Amount",
    },
    modal_apply_payment_label: {
        id: "invoice.receive_payment.apply_payment_label",
        defaultMessage: "Apply payment",
    },
    "payment_nodes_placeholder": {
        id: "price_list.receive_payment.note_placeholder",
        defaultMessage: "Payment notes",
    },
    modal_create_warning: {
        id: "price_list.multi_create.warning",
        defaultMessage: "By clicking on the Create button, you will create {numberOfCustomers}" +
            " orders using the selected payment method (one order and payment per selected customer).",
    }
})

type OwnProps = {
    onHide: () => boolean
    onPrev: () => void
    onCreate: () => void
    show: boolean
    currency: string
    errorMessage?: string
    isProcessingRequest: boolean,
    objectType: PRICE_LIST_OBJECT_TYPE
}

const SelectPaymentMethodModal = (props: Props & IntlProps) => {
    return (
        <>
            <Modal {...props}
                size={"lg"}
                backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FormattedMessage{...messages.modal_title} />
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body className="p-4">
                    <div className="form-group row mb-1">
                        <label className="col-12 col-md-3  col-form-label custom-form-label pb-2" htmlFor="amount">
                            <FormattedMessage{...messages.modal_apply_payment_label} />
                        </label>

                        <div className="col-12 col-md-9">
                            <Toggle
                                disabled={!isTypePriceListObjectWithOptionalPayment(props.objectType)}
                                checked={!props.ignorePaymentStep}
                                icons={false}
                                onChange={() => props.setIgnorePaymentStep(!props.ignorePaymentStep)}
                            />
                        </div>
                    </div>

                    {!props.ignorePaymentStep &&
                        <>
                            <div className="form-group row mb-1">
                                <label className="col-12 col-md-3  col-form-label custom-form-label pb-2" htmlFor="amount">
                                    <FormattedMessage {...messages.modal_amount_label} />
                                </label>

                                <div className="col-12 col-md-9">
                                    <MoneyInputForm
                                        className={"mb-2"}
                                        currency={props.currency}
                                        value={props.balance}
                                        inputClassName={"text-right"}
                                        disabled={true}/>
                                </div>
                            </div>

                            <div className="form-group row mb-1">
                                <label className="col-12 col-md-3  col-form-label custom-form-label pb-2"
                                    htmlFor="paymentType">
                                    <FormattedMessage {...messages.modal_payment_type_label} />
                                </label>

                                <div className="col-12 col-md-9 mb-2">
                                    <PaymentTypeSelect
                                        onChange={e => props.setPaymentType(e)}/>
                                </div>
                            </div>

                            <div className="form-group row mb-0">
                                <label className="col-12 col-md-3  col-form-label custom-form-label pb-2">
                                    <FormattedMessage {...messages.modal_note_label} />
                                </label>

                                <div className="col-12 col-md-9">
                                    <textarea
                                        value={props.paymentNote}
                                        onChange={e => props.setPaymentNote(e.target.value)}
                                        rows={3}
                                        className="form-control"
                                        placeholder={props.intl.formatMessage(messages["payment_nodes_placeholder"])}/>
                                </div>
                            </div>

                            <PaymentCalculationComponent
                                paymentType={props.paymentType}
                                useDefaultCardOnFile={props.useDefaultCardOnFile}
                                onIntegratedPaymentToggle={useCardOnFile => props.setUseDefaultCardOnFile(useCardOnFile)}/>
                        </>
                    }


                    <div className="alert alert-info mt-3">
                        <FormattedMessage {...messages.modal_create_warning}
                            values={{ numberOfCustomers: props.numberOfCustomers }} />
                    </div>
                </Modal.Body>

                <Modal.Footer>
                    {
                        props.errorMessage &&
                        <p className="text-danger">{props.errorMessage}</p>
                    }
                    <Button
                        onClick={props.onPrev}
                        variant="secondary">
                        <FormattedMessage {...messages.modal_back_btn} />
                    </Button>

                    <ActionButton
                        type="button"
                        onClick={(e) => {
                            e.preventDefault()
                            props.onCreate()
                        }}
                        className="btn btn-primary"
                        disabled={props.paymentType === "" && !props.ignorePaymentStep}
                        isLoading={props.isProcessingRequest}
                    >
                        <FormattedMessage {...messages.modal_create_btn} />
                    </ActionButton>
                </Modal.Footer>
            </Modal>
        </>
    )
}

SelectPaymentMethodModal.defaultProps = {
    onHide: () => false,
    onPrev: () => { return },
    onCreate: () => { return },
    show: false,
    currency: "USD",
}

const mapState = (state, props: OwnProps) => {
    return {
        ...props,
        ignorePaymentStep: getIgnorePaymentStep(state),
        balance: getTotalAmount(state),
        paymentType: getPaymentType(state),
        paymentNote: getPaymentNotes(state),
        useDefaultCardOnFile: useDefaultCardOnFile(state),
        numberOfCustomers: getCustomerIdSelection(state).size,
    }
}
const mapDispatch = {
    setPaymentType,
    setPaymentNote,
    setUseDefaultCardOnFile,
    setIgnorePaymentStep,
}
const connector = connect(mapState, mapDispatch)
type Props = ConnectedProps<typeof connector>

export default connector(injectIntl(SelectPaymentMethodModal))


// WEBPACK FOOTER //
// ./src/payment/modal/SelectPaymentMethodModal.tsx